import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { 
  Building2, 
  GraduationCap, 
  HelpCircle,
  Send,
  CheckCircle2,
  Mail,
  Clock
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inquiryTypes = [
  { value: 'wholesale', label: 'Wholesale / Salon Inquiry', icon: Building2 },
  { value: 'education', label: 'March 22nd Masterclass Questions', icon: GraduationCap },
  { value: 'general', label: 'General Question', icon: HelpCircle }
];

export const ContactSection = ({ id }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiry_type: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.inquiry_type || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent! We\'ll get back to you within 4 business hours.');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id={id} className="section bg-[#0F0720] relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-3xl -translate-y-1/2" />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider mb-4 block">
            Get In Touch
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Let's <span className="text-gradient italic">Connect</span>
          </h2>
          <p className="text-purple-200 text-lg max-w-2xl mx-auto">
            Questions about the Perfect Pass keratin iron? Want to bring it to your salon team? 
            We're here to help.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="animate-fade-in-up">
            <div className="form-card">
              {!submitted ? (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Send Us a Message</h3>
                      <p className="text-green-400 text-sm font-medium">We reply within 4 business hours</p>
                    </div>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-purple-300 text-sm mb-2 block">Name *</label>
                        <Input
                          type="text"
                          placeholder="Your name"
                          data-testid="contact-name-input"
                          className="input-glass w-full"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="text-purple-300 text-sm mb-2 block">Email *</label>
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          data-testid="contact-email-input"
                          className="input-glass w-full"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-purple-300 text-sm mb-2 block">Phone (Optional)</label>
                        <Input
                          type="tel"
                          placeholder="+61 400 000 000"
                          data-testid="contact-phone-input"
                          className="input-glass w-full"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="text-purple-300 text-sm mb-2 block">Inquiry Type *</label>
                        <Select 
                          value={formData.inquiry_type}
                          onValueChange={(value) => setFormData({...formData, inquiry_type: value})}
                        >
                          <SelectTrigger 
                            className="input-glass w-full"
                            data-testid="contact-inquiry-select"
                          >
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1E1B4B] border-purple-500/30">
                            {inquiryTypes.map((type) => (
                              <SelectItem 
                                key={type.value} 
                                value={type.value}
                                className="text-purple-200 hover:text-white focus:text-white focus:bg-purple-500/20"
                              >
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-purple-300 text-sm mb-2 block">Message *</label>
                      <Textarea
                        placeholder="Tell us how we can help..."
                        data-testid="contact-message-input"
                        className="input-glass w-full min-h-[120px] resize-none"
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit-btn"
                      className="btn-primary w-full py-4 text-lg flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </>
              ) : (
                <div className="text-center py-12">
                  <div className="checkmark mx-auto mb-4">
                    <CheckCircle2 className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
                  <p className="text-purple-300 mb-2">We'll get back to you within 4 business hours.</p>
                  <p className="text-purple-400 text-sm">Check your email for our response.</p>
                </div>
              )}
            </div>
          </div>

          {/* Contact Info & Quick Actions */}
          <div className="space-y-6 animate-fade-in-up stagger-2">
            {/* Quick Contact Cards */}
            <div className="grid sm:grid-cols-2 gap-4">
              {inquiryTypes.slice(0, 2).map((type, index) => (
                <div 
                  key={type.value}
                  className="glass-card p-6 card-hover"
                >
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                    <type.icon className="w-6 h-6 text-purple-400" />
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{type.label.split(' / ')[0]}</h4>
                  <p className="text-purple-300 text-sm">
                    {type.value === 'wholesale' 
                      ? 'Want to bring Australia\'s best keratin iron to your salon team?'
                      : 'Have questions about the March 22nd Keratin Masterclass?'
                    }
                  </p>
                </div>
              ))}
            </div>

            {/* Response Time Guarantee */}
            <div className="glass-card p-6 border-2 border-green-500/30">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-7 h-7 text-green-400" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white mb-1">Fast Response Guarantee</h4>
                  <p className="text-green-300 text-sm">
                    We respond to all inquiries within 4 business hours. 
                    Your questions about our keratin straightener are important to us!
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="glass-card p-6">
              <h4 className="text-lg font-bold text-white mb-4">Contact Details</h4>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <div className="text-purple-300 text-sm">Email</div>
                    <div className="text-white">support@winamor.com.au</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
